import { Component, ErrorInfo, ReactNode } from 'react'
import posthog from 'posthog-js'
import { Button } from 'ui'
import Sentry from './lib/sentry'

type Props = { children: ReactNode }
type State = { hasError: boolean }

/**
 * Catches a render crash anywhere in the app and shows a way out instead of an
 * empty page. A caught error never reaches `window.onerror`, which is where
 * PostHog's exception autocapture and Sentry's global handler see uncaught
 * ones, so this boundary must report to both itself.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    posthog.captureException(error, { error_boundary: 'app' })
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    })
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="flex flex-col items-center justify-center h-screen w-full gap-4 px-4 text-center">
        <h1 className="text-xl font-semibold text-foreground">
          Something went wrong
        </h1>
        <p className="text-base text-muted-foreground">
          Reload the page to try again.
        </p>
        <Button onClick={() => window.location.reload()}>
          Reload the page
        </Button>
      </div>
    )
  }
}
